import {
  createError,
  defineNuxtRouteMiddleware,
  useResponseHeader,
} from '#imports'

import { useFetchPickersInternal } from '~/scopes/product-core/composables/useFetch/useFetchPickers/composables/useFetchPickersInternal'
import { useFetchProduct } from '~/scopes/product-core/composables/useFetch/useFetchProduct/useFetchProduct'

/**
 * Prefetches critical PDP data (product + pickers) and validates the response.
 *
 * On the CLIENT, this blocks navigation until both endpoints resolve — preventing
 * the page from rendering with stale data during client-side transitions.
 *
 * On the SERVER, this ensures errors from critical endpoints trigger a proper
 * error page (503 for 5xx, passthrough for 4xx) instead of silently rendering
 * a degraded page (e.g. false "Out of Stock" with HTTP 200).
 *
 * Why this matters for SSR + Cloudflare cache:
 * - Without this, a /pickers 500 produces a 200 page showing false OOS
 * - Cloudflare caches that degraded 200 for up to 25 hours
 * - Google bots see a 200 page with OOS content (bad for SEO)
 * - With this, a 500 from /product or /pickers → 503 error page + cache stripped
 *   → Cloudflare does not cache the response
 *
 * Cache header stripping is done HERE, not by the noCacheOnError Nitro plugin.
 * The Nitro plugin hooks into Nitro's `error` event which only fires for
 * unhandled errors. Errors thrown from route middleware are caught by Nuxt
 * (which renders error.vue), so Nitro sees a successful response — the plugin
 * never fires. We must strip cache headers before throwing.
 *
 * The 503 status code (vs 500) signals temporary unavailability to search engines,
 * which avoids de-indexing risk. Decided with Product + SEO (see Confluence:
 * PDP Error Handling — What to display when a backend API returns 500).
 *
 * @see noCacheOnError plugin: packages/nuxt-module-http-cache/src/runtime/server/plugins/noCacheOnError.ts
 * @see Decision doc: https://backmarket.atlassian.net/wiki/spaces/BRO/pages/6374129809
 */
export default defineNuxtRouteMiddleware(
  async function prefetchAndValidate(to) {
    const [product, pickers] = await Promise.all([
      useFetchProduct(to),
      useFetchPickersInternal(to),
    ])

    const error = product.error.value || pickers.error.value
    if (error) {
      const cdnCache = useResponseHeader('CDN-Cache-Control')
      cdnCache.value = 'no-store'

      const browserCache = useResponseHeader('Cache-Control')
      browserCache.value = 'no-store, no-cache, must-revalidate, max-age=0'

      throw createError({
        statusCode:
          error.statusCode && error.statusCode < 500 ? error.statusCode : 503,
        statusMessage: '[PDP] Critical API error',
        cause: error.cause || error.statusMessage || error.message,
        fatal: true,
      })
    }
  },
)
